<!--
  SPOSTAMENTI DELLE AUTO FRA LE SEDI — il disegno della giornata.
  I nodi sono le sedi (o gli indirizzi), gli archi gli spostamenti: una
  freccia per verso fra due sedi, con sopra le corse che la percorrono
  (numero, orario, auto, targa prova, chi rientra a bordo). Sotto, in rosso,
  i driver che restano senza rientro.
  I dati arrivano già pronti da `vistaSedi` (utils/catene.js): lo usano
  l'editor e i dettagli dello spostamento.
-->
<template>
  <section
    v-if="mappa && mappa.corse"
    class="ms"
    aria-label="Spostamenti delle auto fra le sedi"
  >
    <!-- Chiusa di default: si apre quando serve. Il conteggio e gli avvisi
         si vedono anche da chiusa. -->
    <button
      type="button"
      class="ms__head"
      :aria-expanded="aperta ? 'true' : 'false'"
      @click="aperta = !aperta"
    >
      <span
        class="ms__chevron"
        :class="{ 'is-open': aperta }"
        aria-hidden="true"
      >›</span>
      <span class="ms__title">Spostamenti delle auto fra le sedi</span>
      <span
        v-if="mappa.avvisi.length"
        class="ms__alert"
      >{{ mappa.avvisi.length }} senza rientro</span>
      <span class="ms__count">{{ conteggio }} · {{ aperta ? 'nascondi' : 'mostra' }}</span>
    </button>

    <div
      v-show="aperta"
      ref="scroll"
      class="ms__scroll"
    >
      <div
        class="ms__canvas"
        :style="{ width: `${W}px`, height: `${H}px` }"
      >
        <svg
          :width="W"
          :height="H"
          :viewBox="`0 0 ${W} ${H}`"
          class="ms__svg"
          aria-hidden="true"
        >
          <defs>
            <marker
              :id="idFreccia"
              viewBox="0 0 10 10"
              refX="8"
              refY="5"
              markerWidth="7"
              markerHeight="7"
              orient="auto-start-reverse"
            >
              <path
                d="M1 1L9 5L1 9z"
                class="ms__punta"
              />
            </marker>
          </defs>
          <path
            v-for="arco in disegno.archi"
            :key="arco.k"
            :d="arco.d"
            class="ms__arco"
            :class="{ 'ms__arco--singolo': arco.soloSingoli }"
            :stroke-width="arco.spessore"
            :marker-end="`url(#${idFreccia})`"
          />
        </svg>

        <div
          v-for="n in disegno.nodi"
          :key="n.key"
          class="ms__sede"
          :style="{ left: `${n.x}px`, top: `${n.y}px` }"
          :title="n.nome"
        >
          {{ n.nome }}
        </div>

        <ul
          v-for="arco in disegno.archi"
          :key="`e-${arco.k}`"
          :ref="etichetta(arco.k)"
          class="ms__etichetta"
          :style="posEtichette[arco.k]
            ? { left: `${posEtichette[arco.k].x}px`, top: `${posEtichette[arco.k].y}px` }
            : {
              left: `${arco.lx}px`,
              top: `${arco.ly}px`,
              transform: `translate(${arco.ax * 100}%, ${arco.ay * 100}%)`,
            }"
        >
          <li
            v-for="c in arco.corse"
            :key="c.id"
            class="ms__corsa"
          >
            <b class="ms__n">{{ c.n }}</b>
            <span class="ms__ora">{{ c.ora || '--:--' }}</span>
            <span class="ms__auto">{{ c.auto || 'auto da indicare' }}</span>
            <span
              v-if="c.prova"
              class="ms__tag ms__tag--prova"
            >targa prova</span>
            <span
              v-if="c.aBordo"
              class="ms__tag"
            >+{{ c.aBordo }} a bordo</span>
            <span
              v-if="!c.catena"
              class="ms__tag ms__tag--muted"
            >{{ c.mezzi ? 'rientro coi mezzi' : 'con jolly' }}</span>
          </li>
        </ul>
      </div>
    </div>

    <p
      v-show="aperta"
      class="ms__legenda"
    >
      <span class="ms__leg ms__leg--catena" /> a catena
      <span class="ms__leg ms__leg--singolo" /> singolo (con jolly)
    </p>

    <ul
      v-if="aperta && mappa.avvisi.length"
      class="ms__avvisi"
    >
      <li
        v-for="a in mappa.avvisi"
        :key="a"
      >
        {{ a }}
      </li>
    </ul>
  </section>
</template>

<script>
import { computed, ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';

let istanze = 0;

export default {
  name: 'MappaSpostamenti',
  props: {
    // Uscita di `vistaSedi` (utils/catene.js).
    mappa: { type: Object, default: null },
  },
  setup(props) {
    const idFreccia = `ms-freccia-${(istanze += 1)}`;
    const aperta = ref(false);
    // Larghezza: quella che c'è (fra 760 e 1400 px), così il disegno si
    // allarga in orizzontale invece di crescere in altezza.
    const scroll = ref(null);
    const larghezza = ref(760);
    const misura = () => {
      const w = scroll.value?.clientWidth || 0;
      if (w) larghezza.value = Math.max(760, Math.min(1400, w));
    };
    let osservatore = null;
    onMounted(() => {
      if (typeof ResizeObserver !== 'undefined' && scroll.value) {
        osservatore = new ResizeObserver(misura);
        osservatore.observe(scroll.value);
      }
    });
    onBeforeUnmount(() => osservatore?.disconnect());
    watch(aperta, (v) => { if (v) nextTick(() => misura()); });
    const W = computed(() => larghezza.value);
    const MARGINE = 80; // spazio sopra e sotto le sedi, per le etichette
    const nNodi = computed(() => props.mappa?.nodi?.length || 0);

    // Le sedi: due una di fronte all'altra, da tre in su su un'ellisse; poi
    // il disegno si stringe all'altezza che serve.
    const grezze = computed(() => {
      const n = nNodi.value;
      if (n === 0) return [];
      const w = W.value;
      if (n === 1) return [{ x: w / 2, y: 0 }];
      if (n === 2) return [{ x: Math.max(170, w * 0.22), y: 0 }, { x: w - Math.max(170, w * 0.22), y: 0 }];
      // Un'ellisse larga e bassa (nessuna freccia passa sotto una sede): si
      // ruota il giro di sedi in modo che venga il più largo possibile, poi
      // lo si stende su tutta la larghezza.
      let giro = null;
      for (let k = 0; k < 8; k += 1) {
        const a0 = Math.PI + (k * Math.PI) / (4 * n);
        const ang = Array.from({ length: n }, (_, i) => a0 + (2 * Math.PI * i) / n);
        const cs = ang.map(Math.cos);
        const sn = ang.map(Math.sin);
        const c = { cs, sn, minC: Math.min(...cs), minS: Math.min(...sn) };
        c.sx = Math.max(...cs) - c.minC;
        c.sy = Math.max(...sn) - c.minS || 1;
        if (!giro || c.sx / c.sy > giro.sx / giro.sy + 1e-6) giro = c;
      }
      const alto = Math.min(200, 50 + n * 30);
      return giro.cs.map((cv, i) => ({
        x: 120 + ((cv - giro.minC) / giro.sx) * (w - 240),
        y: ((giro.sn[i] - giro.minS) / giro.sy) * alto,
      }));
    });
    const posizioni = computed(() => {
      const ys = grezze.value.map((p) => p.y);
      const minY = ys.length ? Math.min(...ys) : 0;
      return grezze.value.map((p) => ({ x: p.x, y: p.y - minY + MARGINE }));
    });
    const altezzaBase = computed(() => {
      const ys = posizioni.value.map((p) => p.y);
      return (ys.length ? Math.max(...ys) : 0) + MARGINE;
    });
    // Le etichette spostate per non sovrapporsi possono chiedere più spazio.
    const extraH = ref(0);
    const H = computed(() => altezzaBase.value + extraH.value);

    // Quanto spazio occupa una sede attorno al suo centro, nella direzione
    // (dx, dy): la freccia si ferma al bordo, non sotto il nome.
    const bordo = (dx, dy) => {
      const len = Math.hypot(dx, dy) || 1;
      const c = dx / len;
      const s = dy / len;
      return 1 / Math.sqrt((c * c) / (84 * 84) + (s * s) / (22 * 22)) + 4;
    };

    const disegno = computed(() => {
      const m = props.mappa;
      if (!m) return { nodi: [], archi: [] };
      const pos = posizioni.value;
      const nodi = m.nodi.map((n, i) => ({ ...n, ...pos[i] }));
      const versi = new Set(m.archi.map((a) => `${a.da}>${a.a}`));
      const archi = m.archi.map((arco) => {
        // Andata e ritorno fra le stesse sedi: due archi ben separati, uno
        // per lato. Un arco da solo resta quasi dritto.
        const p0 = pos[arco.da];
        const p1 = pos[arco.a];
        const dx = p1.x - p0.x;
        const dy = p1.y - p0.y;
        const len = Math.hypot(dx, dy) || 1;
        const piega = versi.has(`${arco.a}>${arco.da}`) ? Math.max(60, Math.min(100, len * 0.14)) : 28;
        // Le due direzioni fra le stesse sedi si piegano da lati opposti.
        const c = { x: (p0.x + p1.x) / 2 + (-dy / len) * piega, y: (p0.y + p1.y) / 2 + (dx / len) * piega };
        const verso = (from, to) => {
          const vx = to.x - from.x;
          const vy = to.y - from.y;
          const l = Math.hypot(vx, vy) || 1;
          const r = bordo(vx, vy);
          return { x: from.x + (vx / l) * r, y: from.y + (vy / l) * r };
        };
        const s = verso(p0, c);
        const e = verso(p1, c);
        // L'etichetta sta FUORI dalla curva, dal lato verso cui si piega:
        // così andata e ritorno non si sovrappongono.
        const nx = -dy / len;
        const ny = dx / len;
        const mx = 0.25 * s.x + 0.5 * c.x + 0.25 * e.x + nx * 16;
        const my = 0.25 * s.y + 0.5 * c.y + 0.25 * e.y + ny * 16;
        // Da che parte del punto sta l'etichetta (frazioni della sua misura).
        let ax = -0.5;
        let ay = -0.5;
        if (Math.abs(nx) >= Math.abs(ny)) ax = nx > 0 ? 0 : -1;
        else ay = ny > 0 ? 0 : -1;
        return {
          k: `${arco.da}>${arco.a}`,
          d: `M ${s.x} ${s.y} Q ${c.x} ${c.y} ${e.x} ${e.y}`,
          lx: mx,
          ly: my,
          ax,
          ay,
          spessore: 1.5 + Math.min(arco.corse.length, 5) * 0.5,
          soloSingoli: arco.corse.every((x) => !x.catena),
          s,
          c,
          e,
          nx,
          ny,
          corse: arco.corse,
        };
      });
      return { nodi, archi };
    });

    // Etichette che si toccano (fra loro o con una sede): si misurano già
    // disegnate e si scostano in verticale finché non si separano.
    const elementi = new Map();
    const etichetta = (k) => (el) => {
      if (el) elementi.set(k, el);
      else elementi.delete(k);
    };
    const posEtichette = ref({});
    const sistemaEtichette = () => {
      if (!aperta.value) return;
      const { nodi, archi } = disegno.value;
      const pad = (r, m) => ({ x0: r.x0 - m, x1: r.x1 + m, y0: r.y0 - m, y1: r.y1 + m });
      const tocca = (a, b) => a.x0 < b.x1 && b.x0 < a.x1 && a.y0 < b.y1 && b.y0 < a.y1;
      const dentro = (p, r) => p.x > r.x0 && p.x < r.x1 && p.y > r.y0 && p.y < r.y1;
      const sedi = nodi.map((n) => ({ x0: n.x - 92, x1: n.x + 92, y0: n.y - 26, y1: n.y + 26 }));
      const punto = (a, t) => ({
        x: (1 - t) ** 2 * a.s.x + 2 * (1 - t) * t * a.c.x + t ** 2 * a.e.x,
        y: (1 - t) ** 2 * a.s.y + 2 * (1 - t) * t * a.c.y + t ** 2 * a.e.y,
      });
      const tracce = archi.map((a) => Array.from({ length: 30 }, (_, i) => punto(a, i / 29)));
      const W0 = W.value;

      // Per ogni freccia si provano più posti lungo la curva, e da tutte e
      // due le parti; vince quello che non copre sedi, altre etichette o
      // altre frecce. Prima le etichette più grandi.
      const ordine = archi.map((a, i) => i).sort((i, j) => archi[j].corse.length - archi[i].corse.length);
      const messe = [];
      const pos = {};
      ordine.forEach((i) => {
        const a = archi[i];
        const el = elementi.get(a.k);
        const w = el?.offsetWidth || 160;
        const h = el?.offsetHeight || 24;
        let meglio = null;
        [0.5, 0.4, 0.6, 0.3, 0.7, 0.22, 0.78].forEach((t) => {
          [1, -1].forEach((lato) => {
            const p = punto(a, t);
            const ax = lato > 0 ? a.ax : -1 - a.ax;
            const ay = lato > 0 ? a.ay : -1 - a.ay;
            const x0 = Math.max(4, Math.min(W0 - w - 4, p.x + a.nx * 16 * lato + ax * w));
            const y0 = Math.max(4, p.y + a.ny * 16 * lato + ay * h);
            const r = { x0, x1: x0 + w, y0, y1: y0 + h };
            const largo = pad(r, 6);
            let costo = Math.abs(t - 0.5) * 40 + (lato < 0 ? 18 : 0);
            sedi.forEach((sd) => { if (tocca(largo, sd)) costo += 1000; });
            messe.forEach((m) => { if (tocca(largo, m)) costo += 1000; });
            tracce.forEach((tr, j) => {
              tr.forEach((q) => { if (dentro(q, pad(r, 3))) costo += j === i ? 12 : 30; });
            });
            if (!meglio || costo < meglio.costo) meglio = { costo, r, lato, ay };
          });
        });
        messe.push(meglio.r);
        pos[a.k] = { ...meglio.r, h, dir: meglio.ay === 0 ? 1 : -1 };
      });

      // Se proprio non c'è posto: si scostano in verticale.
      const box = Object.values(pos);
      for (let giro = 0; giro < 60; giro += 1) {
        let mosso = false;
        for (let i = 0; i < box.length; i += 1) {
          for (let j = i + 1; j < box.length; j += 1) {
            const [su, giu] = box[i].y0 <= box[j].y0 ? [box[i], box[j]] : [box[j], box[i]];
            if (tocca(pad(su, 4), giu)) {
              const o = su.y1 + 8 - giu.y0;
              if (su.dir < 0 && su.y0 - o >= 4) {
                su.y0 -= o;
                su.y1 -= o;
              } else {
                giu.y0 += o;
                giu.y1 += o;
              }
              mosso = true;
            }
          }
        }
        if (!mosso) break;
      }
      posEtichette.value = Object.fromEntries(Object.entries(pos).map(([k, r]) => [k, { x: r.x0, y: r.y0 }]));
      const fondo = box.length ? Math.max(...box.map((b) => b.y1)) : 0;
      extraH.value = Math.max(0, Math.ceil(fondo + 8 - altezzaBase.value));
    };
    watch([disegno, aperta], () => { nextTick(() => sistemaEtichette()); });

    const conteggio = computed(() => {
      const m = props.mappa;
      if (!m) return '';
      const corse = `${m.corse} ${m.corse === 1 ? 'corsa' : 'corse'}`;
      const sedi = `${m.nodi.length} ${m.nodi.length === 1 ? 'sede' : 'sedi'}`;
      return `${corse} · ${sedi}`;
    });

    return {
      W, H, idFreccia, disegno, conteggio, aperta, scroll, etichetta, posEtichette,
    };
  },
};
</script>

<style scoped>
.ms {
  --ms-accent: var(--svc-fleet, var(--brand-violet, #4b30f2));
  --ms-accent-bg: var(--svc-fleet-bg, rgba(75, 48, 242, 0.08));
  --ms-muted: var(--jt-muted, var(--text-secondary, #64748b));
  --ms-line: var(--jt-border, var(--border, #e2e8f0));
  --ms-surface: var(--surface, #fff);
  --ms-err: var(--state-err, #b91c1c);
  border: 1px solid var(--ms-line);
  border-radius: 10px;
  padding: 14px 16px;
  background: var(--ms-surface);
}
.ms__head {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
}
.ms__head:focus-visible { outline: 2px solid var(--ms-accent); outline-offset: 4px; border-radius: 4px; }
.ms__chevron {
  display: inline-block;
  width: 14px;
  font-size: 18px;
  line-height: 1;
  color: var(--ms-accent);
  transition: transform 0.15s ease;
}
.ms__chevron.is-open { transform: rotate(90deg); }
.ms__alert {
  padding: 1px 8px;
  border-radius: 999px;
  background: rgba(185, 28, 28, 0.1);
  color: var(--ms-err);
  font-size: 12px;
  font-weight: 600;
}
.ms__scroll {
  margin-top: 8px;
}
.ms__title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
}
.ms__count {
  margin-left: auto;
  font-size: 12px;
  color: var(--ms-muted);
  font-variant-numeric: tabular-nums;
}
.ms__scroll {
  overflow-x: auto;
}
@media (prefers-reduced-motion: reduce) {
  .ms__chevron { transition: none; }
}
.ms__canvas {
  position: relative;
  margin: 0 auto;
}
.ms__svg {
  position: absolute;
  inset: 0;
}
.ms__arco {
  fill: none;
  stroke: var(--ms-accent);
}
.ms__arco--singolo {
  stroke-dasharray: 7 5;
}
.ms__punta {
  fill: var(--ms-accent);
}
.ms__sede {
  position: absolute;
  transform: translate(-50%, -50%);
  max-width: 168px;
  padding: 8px 14px;
  border: 2px solid var(--ms-accent);
  border-radius: 10px;
  background: var(--ms-surface);
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: normal;
}
.ms__etichetta {
  position: absolute;
  z-index: 1;
  margin: 0;
  padding: 5px 8px;
  list-style: none;
  display: grid;
  gap: 3px;
  border-radius: 8px;
  background: var(--ms-surface);
  box-shadow: 0 0 0 1px var(--ms-line);
  font-size: 11.5px;
  white-space: nowrap;
  word-break: normal;
}
.ms__corsa {
  display: flex;
  align-items: center;
  gap: 5px;
}
.ms__n {
  display: inline-grid;
  place-items: center;
  width: 17px;
  height: 17px;
  border-radius: 50%;
  background: var(--ms-accent);
  color: #fff;
  font-size: 10px;
}
.ms__ora {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.ms__auto {
  color: var(--ms-muted);
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ms__tag {
  padding: 0 6px;
  border-radius: 999px;
  background: var(--ms-accent-bg);
  color: var(--ms-accent);
  font-weight: 600;
}
.ms__tag--prova {
  background: rgba(194, 65, 12, 0.12);
  color: var(--state-attn, #c2410c);
}
.ms__tag--muted {
  background: transparent;
  color: var(--ms-muted);
  border: 1px solid var(--ms-line);
}
.ms__legenda {
  display: flex;
  align-items: center;
  gap: 6px 10px;
  flex-wrap: wrap;
  margin: 8px 0 0;
  font-size: 11.5px;
  color: var(--ms-muted);
}
.ms__leg {
  display: inline-block;
  width: 22px;
  height: 0;
  border-top: 2px solid var(--ms-accent);
}
.ms__leg--singolo {
  border-top-style: dashed;
  margin-left: 8px;
}
.ms__avvisi {
  margin: 10px 0 0;
  padding: 8px 12px;
  list-style: none;
  border-radius: 6px;
  background: rgba(185, 28, 28, 0.07);
  box-shadow: inset 3px 0 0 var(--ms-err);
  color: var(--ms-err);
  font-size: 12.5px;
  font-weight: 600;
}
</style>
